const NAME_JOINER_WORDS = new Set([
  "von",
  "van",
  "der",
  "den",
  "de",
  "la",
  "le",
  "of",
  "di",
  "da",
  "del",
  "della"
]);

const ZOOM_THRESHOLD_FULL_LABEL = 5;
const IDLE_RESET_DELAY_MS = 12000;

const ACTIVE_STATE = ["boolean", ["feature-state", "active"], false];
const HOVER_STATE = ["boolean", ["feature-state", "hover"], false];

function isValidCoordinatePair(lng, lat) {
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
    return false;
  }
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
    return false;
  }
  return true;
}

function deriveInitials(name) {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  return parts
    .map((part, index) => {
      if (!part) return "";
      const initial = part[0] || "";
      if (index === 0) return initial.toUpperCase();
      return NAME_JOINER_WORDS.has(part.toLowerCase())
        ? initial.toLowerCase()
        : initial.toUpperCase();
    })
    .join("");
}

function cancelIdleReset() {
  if (window.mapState.idleResetTimer) {
    clearTimeout(window.mapState.idleResetTimer);
    window.mapState.idleResetTimer = null;
  }
}

function scheduleIdleReset() {
  cancelIdleReset();
  if (window.mapState.idleResetEnabled === false) return;
  if (!window.mapState.bounds) return;
  if (typeof window.mapState.hoveredMarkerIndex === "number") return;
  const delay = window.mapState.idleResetDelayMs || IDLE_RESET_DELAY_MS;
  window.mapState.idleResetTimer = window.setTimeout(() => {
    resetToAllMarkers({});
  }, delay);
}

function resetToAllMarkers({ duration = 1200 } = {}) {
  const { map, bounds, projection } = window.mapState;
  if (!map || !bounds || bounds.isEmpty()) return;

  window.mapState.idleResetTimer = null;

  if (map.getSource(PEOPLE_SOURCE_ID)) {
    if (typeof window.mapState.hoveredMarkerIndex === "number") {
      map.setFeatureState(
        { source: PEOPLE_SOURCE_ID, id: window.mapState.hoveredMarkerIndex },
        { hover: false }
      );
    }
  }

  window.mapState.hoveredMarkerIndex = null;
  setMarkerActive(null);
  if (Array.isArray(window.mapState.linksWithCoordinates)) {
    window.mapState.linksWithCoordinates.forEach(link =>
      link.classList.remove("is-hovered")
    );
  }
  const target = bounds.clone ? bounds.clone() : bounds;
  const mapProjection = projection || "mercator";
  const maxZoom = mapProjection === "globe" ? 3.2 : 5;

  if (typeof target.getSouthWest === "function" && typeof target.getNorthEast === "function") {
    const sw = target.getSouthWest();
    const ne = target.getNorthEast();
    if (!isValidCoordinatePair(sw.lng, sw.lat) || !isValidCoordinatePair(ne.lng, ne.lat)) {
      console.warn("resetToAllMarkers: invalid target bounds", target);
      return;
    }
  }

  try {
    map.fitBounds(target, {
      padding: { top: 80, bottom: 80, left: 80, right: 80 },
      maxZoom,
      duration,
      essential: true
    });
  } catch (error) {
    console.error("resetToAllMarkers: unable to fit bounds", error, target);
  }
}

function initializeMap({
  containerId,
  accessToken,
  center = [10.4515, 51.1657],
  zoom = 3,
  padding = { right: 0, left: window.innerWidth * 0.33 },
  style = "mapbox://styles/mapbox/light-v11",
  scrollToLink = false,
  projection = "mercator",
  idleResetEnabled = true,
  useInitials = true
}) {
  // Clear the map container to ensure it's empty for Mapbox GL
  const mapContainer = document.getElementById(containerId);
  if (mapContainer) {
    mapContainer.innerHTML = '';
  }

  mapboxgl.accessToken = accessToken;
  window.mapState.scrollToLink = scrollToLink;
  window.mapState.idleResetDelayMs = IDLE_RESET_DELAY_MS;
  window.mapState.idleResetEnabled = idleResetEnabled;
  window.mapState.projection = projection;
  window.mapState.useInitials = useInitials;
  const map = new mapboxgl.Map({
    container: containerId,
    style,
    center,
    zoom,
    padding,
    projection
  });

  if (projection === "globe") {
    map.on("style.load", () => {
      map.setFog(null);
    });
  }

  map.on("dragstart", cancelIdleReset);
  map.on("zoomstart", cancelIdleReset);
  map.on("moveend", scheduleIdleReset);

  return map;
}

const PEOPLE_SOURCE_ID = "people-markers";
const PEOPLE_CIRCLE_LAYER_ID = "people-markers-circle";
const PEOPLE_LABEL_LAYER_INITIALS_ID = "people-markers-label-initials";
const PEOPLE_LABEL_LAYER_FULL_ID = "people-markers-label-full";
const PEOPLE_LABEL_LAYER_FULL_HOVER_ID = "people-markers-label-full-hover";

function setupMarkers({ bounds }) {
  const features = [];
  window.mapState.linksWithCoordinates.forEach((link, index) => {
    const lng = parseFloat(link.getAttribute("data-lng"));
    const lat = parseFloat(link.getAttribute("data-lat"));
    const label =
      link.getAttribute("data-marker-label") || link.textContent.trim();
    const initialsAttr = link.getAttribute("data-marker-initials");
    const initials =
      (initialsAttr && initialsAttr.trim()) || deriveInitials(label);

    if (isValidCoordinatePair(lng, lat)) {
      features.push({
        type: "Feature",
        id: index,
        geometry: {
          type: "Point",
          coordinates: [lng, lat]
        },
        properties: {
          index,
          label,
          initials,
          url: link.href
        }
      });
      bounds.extend([lng, lat]);
    } else {
      console.warn(
        `Invalid coordinates for link at index ${index} (${label})`,
        "lng:",
        link.getAttribute("data-lng"),
        "lat:",
        link.getAttribute("data-lat")
      );
    }
  });

  const featureCollection = {
    type: "FeatureCollection",
    features
  };

  window.mapState.featureCollection = featureCollection;
  window.mapState.resetToAllMarkers = resetToAllMarkers;

  if (!features.length) {
    console.warn("No valid coordinates found for biography markers");
    return;
  }

  const hydrateLayers = () => {
    ensureMarkerLayers({ data: featureCollection });
    alignMapToBounds(bounds);
    resetToAllMarkers({ duration: 0 });
    scheduleIdleReset();
  };

  if (window.mapState.map.isStyleLoaded()) {
    hydrateLayers();
  } else {
    window.mapState.map.once("load", hydrateLayers);
  }
}

function setMarkerActive(activeIndex) {
  window.mapState.activeMarkerIndex = activeIndex;

  const map = window.mapState.map;
  if (!map) return;

  const source = map.getSource(PEOPLE_SOURCE_ID);
  if (!source) {
    window.mapState.pendingActiveIndex = activeIndex;
    return;
  }

  const previousActive = window.mapState.lastActiveMarkerIndex;
  if (typeof previousActive === "number" && previousActive !== activeIndex) {
    map.setFeatureState(
      { source: PEOPLE_SOURCE_ID, id: previousActive },
      { active: false }
    );
  }
  if (typeof activeIndex === "number") {
    map.setFeatureState(
      { source: PEOPLE_SOURCE_ID, id: activeIndex },
      { active: true }
    );
    window.mapState.lastActiveMarkerIndex = activeIndex;
  } else {
    window.mapState.lastActiveMarkerIndex = null;
  }
}

function setMarkerHover({ index, isHovering }) {
  if (isHovering) {
    cancelIdleReset();
  }

  const map = window.mapState.map;
  if (!map || !map.getSource(PEOPLE_SOURCE_ID)) {
    return;
  }

  map.setFeatureState(
    { source: PEOPLE_SOURCE_ID, id: index },
    { hover: isHovering }
  );

  if (isHovering) {
    window.mapState.hoveredMarkerIndex = index;
    return;
  }

  if (window.mapState.hoveredMarkerIndex === index) {
    window.mapState.hoveredMarkerIndex = null;
  } else if (typeof window.mapState.hoveredMarkerIndex !== "number") {
    window.mapState.hoveredMarkerIndex = null;
  }

  if (window.mapState.hoveredMarkerIndex === null) {
    scheduleIdleReset();
  }
}

function setActiveLink(activeIndex) {
  cancelIdleReset();

  const activeLink = window.mapState.linksWithCoordinates[activeIndex];
  if (!activeLink) return;

  window.mapState.linksWithCoordinates.forEach((link, index) => {
    link.classList.remove("selected");
    link.style.fontWeight = "";
    link.classList.remove("is-hovered");

    if (index === activeIndex) {
      link.classList.add("selected");
      link.style.fontWeight = "bold";
    }
  });

  setMarkerActive(activeIndex);

  // scroll to the corresponding space/map link
  if (window.mapState.scrollToLink) {
    const linkOffset = activeLink.getBoundingClientRect().top + window.scrollY;
    const viewportCenter = window.innerHeight / 2;
    const scrollPosition = linkOffset - viewportCenter + (activeLink.offsetHeight / 2);
  
    window.scrollTo({
      top: scrollPosition,
      behavior: "smooth"
    });
  }

  const lng = parseFloat(activeLink.getAttribute("data-lng"));
  const lat = parseFloat(activeLink.getAttribute("data-lat"));
  if (isValidCoordinatePair(lng, lat)) {
    window.mapState.map.flyTo({
      center: [lng, lat],
      zoom: 6,
      padding: {
        left: 0,
        right: 0,
        top: 0,
        bottom: 0
      },
      duration: 1500
    });
  }

  scheduleIdleReset();
}

function setupScrollListener() {
  const isMobile = window.innerWidth < 768;
  if (!isMobile) return;

  const onScroll = () => {
    const viewportBottom = window.innerHeight;
    let closestIndex = 0;
    let closestDistance = Infinity;

    window.mapState.linksWithCoordinates.forEach((link, index) => {
      const rect = link.getBoundingClientRect();
      const distance = Math.abs(
        viewportBottom - rect.bottom - viewportBottom * 0.5
      );
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    setActiveLink(closestIndex);
  };

  window.addEventListener("scroll", onScroll);
}

function setupLinkMouseEvents() {
  const flyToLocation = (link, index) => {
    cancelIdleReset();
    link.classList.add("is-hovered");
    setMarkerHover({ index, isHovering: true });
    const lng = parseFloat(link.getAttribute("data-lng"));
    const lat = parseFloat(link.getAttribute("data-lat"));
    if (isValidCoordinatePair(lng, lat)) {
      window.mapState.map.flyTo({
        center: [lng, lat],
        zoom: 6,
        padding: {
          left: 0,
          right: 0,
          top: 0,
          bottom: 0
        },
        duration: 1500
      });
    }
  };

  window.mapState.linksWithCoordinates.forEach((link, index) => {
    link.addEventListener("mouseenter", () =>
      flyToLocation(link, index)
    );
    link.addEventListener("mouseleave", () => {
      setMarkerHover({ index, isHovering: false });
      link.classList.remove("is-hovered");
      setMarkerActive(null);
      // Stay on the hovered person; only fly back out to the globe after the
      // pointer has been away from any name and the map idle for the full delay.
      scheduleIdleReset();
    });
    link.addEventListener("focus", () => flyToLocation(link, index));
    link.addEventListener("blur", () => {
      setMarkerHover({ index, isHovering: false });
      link.classList.remove("is-hovered");
      setMarkerActive(null);
      scheduleIdleReset();
    });
  });
}

function ensureMarkerLayers({ data }) {
  const map = window.mapState.map;
  if (!map) return;

  const applyData = () => {
    if (map.getSource(PEOPLE_SOURCE_ID)) {
      map.getSource(PEOPLE_SOURCE_ID).setData(data);
    } else {
      map.addSource(PEOPLE_SOURCE_ID, {
        type: "geojson",
        data
      });
      addMarkerLayers(map);
      registerMapLayerEvents(map);
      window.mapState.layersInitialized = true;
    }

    if (typeof window.mapState.pendingActiveIndex === "number") {
      const pendingIndex = window.mapState.pendingActiveIndex;
      window.mapState.pendingActiveIndex = null;
      setMarkerActive(pendingIndex);
    }
  };

  if (map.isStyleLoaded()) {
    applyData();
  } else {
    map.once("load", applyData);
  }
}

function addMarkerLayers(map) {
  if (!map.getLayer(PEOPLE_CIRCLE_LAYER_ID)) {
    map.addLayer({
      id: PEOPLE_CIRCLE_LAYER_ID,
      type: "circle",
      source: PEOPLE_SOURCE_ID,
      paint: {
        "circle-radius": [
          "interpolate",
          ["linear"],
          ["zoom"],
          2,
          [
            "case",
            ["boolean", ["feature-state", "active"], false],
            5.5,
            ["boolean", ["feature-state", "hover"], false],
            5,
            4.2
          ],
          4,
          [
            "case",
            ["boolean", ["feature-state", "active"], false],
            6.5,
            ["boolean", ["feature-state", "hover"], false],
            6,
            5.2
          ],
          6,
          [
            "case",
            ["boolean", ["feature-state", "active"], false],
            8,
            ["boolean", ["feature-state", "hover"], false],
            7,
            6
          ],
          9,
          [
            "case",
            ["boolean", ["feature-state", "active"], false],
            9.5,
            ["boolean", ["feature-state", "hover"], false],
            8.5,
            7.5
          ]
        ],
        "circle-color": [
          "case",
          ["boolean", ["feature-state", "active"], false],
          "#ff0000",
          ["boolean", ["feature-state", "hover"], false],
          "#ff0000",
          "#000000"
        ],
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 2
      }
    });
  }

  if (!map.getLayer(PEOPLE_LABEL_LAYER_INITIALS_ID)) {
    map.addLayer({
      id: PEOPLE_LABEL_LAYER_INITIALS_ID,
      type: "symbol",
      source: PEOPLE_SOURCE_ID,
      layout: {
        "text-field": ["get", "initials"],
        "text-size": [
          "interpolate",
          ["linear"],
          ["zoom"],
          3,
          11,
          6,
          13,
          9,
          16
        ],
        "text-variable-anchor": [
          "top",
          "bottom",
          "left",
          "right",
          "top-left",
          "top-right",
          "bottom-left",
          "bottom-right"
        ],
        "text-radial-offset": 0.9,
        "text-justify": "auto",
        "text-allow-overlap": false,
        "text-ignore-placement": false,
        "text-optional": true,
        "text-padding": 1,
        "text-font": ["DIN Pro Medium", "Arial Unicode MS Regular"]
      },
      paint: {
        "text-color": "#111111",
        "text-halo-color": "#f5f5f5",
        "text-halo-width": 1.2,
        "text-halo-blur": 0.1,
        "text-opacity": window.mapState.useInitials ? [
          "step",
          ["zoom"],
          [
            "case",
            ["any", ACTIVE_STATE, HOVER_STATE],
            0,
            1
          ],
          ZOOM_THRESHOLD_FULL_LABEL,
          0
        ] : 0
      }
    });
  }

  if (!map.getLayer(PEOPLE_LABEL_LAYER_FULL_ID)) {
    map.addLayer({
      id: PEOPLE_LABEL_LAYER_FULL_ID,
      type: "symbol",
      source: PEOPLE_SOURCE_ID,
      layout: {
        "text-field": ["get", "label"],
        "text-size": [
          "interpolate",
          ["linear"],
          ["zoom"],
          3,
          11,
          6,
          13,
          9,
          16
        ],
        "text-variable-anchor": [
          "top",
          "bottom",
          "left",
          "right",
          "top-left",
          "top-right",
          "bottom-left",
          "bottom-right"
        ],
        "text-radial-offset": 1,
        "text-justify": "auto",
        "text-allow-overlap": false,
        "text-ignore-placement": false,
        "text-optional": true,
        "text-padding": 1,
        "text-font": ["DIN Pro Medium", "Arial Unicode MS Regular"]
      },
      paint: {
        "text-color": [
          "case",
          ACTIVE_STATE,
          "#ff0000",
          HOVER_STATE,
          "#ff0000",
          "#111111"
        ],
        "text-halo-color": [
          "case",
          ["any", ACTIVE_STATE, HOVER_STATE],
          "#ffffff",
          "#f5f5f5"
        ],
        "text-halo-width": [
          "case",
          ACTIVE_STATE,
          2.6,
          HOVER_STATE,
          2.3,
          1.2
        ],
        "text-halo-blur": 0.1,
        // Keep the overview clean: full place labels only appear once zoomed in
        // past the threshold (below it they collide into an unreadable pile in
        // dense cities). At the overview the points read as plain dots, and a
        // label is revealed on hover via the hover layer below. Applies to both
        // maps now, so the biographies and spaces maps behave consistently.
        "text-opacity": [
          "step",
          ["zoom"],
          0,
          ZOOM_THRESHOLD_FULL_LABEL,
          1
        ]
      }
    });
  }

  if (!map.getLayer(PEOPLE_LABEL_LAYER_FULL_HOVER_ID)) {
    map.addLayer({
      id: PEOPLE_LABEL_LAYER_FULL_HOVER_ID,
      type: "symbol",
      source: PEOPLE_SOURCE_ID,
      layout: {
        "text-field": ["get", "label"],
        "text-size": [
          "interpolate",
          ["linear"],
          ["zoom"],
          3,
          11,
          6,
          13,
          9,
          16
        ],
        "text-variable-anchor": [
          "top",
          "bottom",
          "left",
          "right",
          "top-left",
          "top-right",
          "bottom-left",
          "bottom-right"
        ],
        "text-radial-offset": 1,
        "text-justify": "auto",
        "text-allow-overlap": true,
        "text-ignore-placement": true,
        "text-optional": false,
        "text-padding": 0.5,
        "symbol-sort-key": 4,
        "text-font": ["DIN Pro Medium", "Arial Unicode MS Regular"]
      },
      paint: {
        "text-color": "#ff0000",
        "text-halo-color": "#ffffff",
        "text-halo-width": 2.6,
        "text-halo-blur": 0.05,
        "text-opacity": window.mapState.useInitials ? [
          "step",
          ["zoom"],
          [
            "case",
            ["any", ACTIVE_STATE, HOVER_STATE],
            1,
            0
          ],
          ZOOM_THRESHOLD_FULL_LABEL,
          0
        ] : [
          "case",
          ["any", ACTIVE_STATE, HOVER_STATE],
          1,
          0
        ]
      }
    });
  }
}

function registerMapLayerEvents(map) {
  const interactiveLayers = [
    PEOPLE_CIRCLE_LAYER_ID,
    PEOPLE_LABEL_LAYER_INITIALS_ID,
    PEOPLE_LABEL_LAYER_FULL_ID,
    PEOPLE_LABEL_LAYER_FULL_HOVER_ID
  ];

  interactiveLayers.forEach(layerId => {
    map.on("mouseenter", layerId, event => {
      map.getCanvas().style.cursor = "pointer";
      const feature = event.features && event.features[0];
      if (!feature) return;
      const index = feature.id;
      const currentlyHovered = window.mapState.hoveredMarkerIndex;
      if (typeof currentlyHovered === "number" && currentlyHovered !== index) {
        setMarkerHover({ index: currentlyHovered, isHovering: false });
      }
      setMarkerHover({ index, isHovering: true });
    });

    map.on("mouseleave", layerId, () => {
      map.getCanvas().style.cursor = "";
      const hoveredIndex = window.mapState.hoveredMarkerIndex;
      if (typeof hoveredIndex === "number") {
        setMarkerHover({ index: hoveredIndex, isHovering: false });
      }
      // Keep the current view; only idle-reset to the globe after the delay.
      scheduleIdleReset();
    });

    map.on("click", layerId, event => {
      const feature = event.features && event.features[0];
      if (!feature) return;
      const index = feature.id;
      const link = window.mapState.linksWithCoordinates[index];
      if (!link) return;

      window.mapState.setActiveLink(index);
      window.location.href = link.href;
    });
  });
}

function alignMapToBounds(bounds) {
  if (!bounds) return;

  let targetBounds;
  if (typeof bounds.getSouthWest === "function" && typeof bounds.getNorthEast === "function") {
    targetBounds = bounds.clone ? bounds.clone() : bounds;
  } else {
    console.warn("alignMapToBounds: unexpected bounds shape", bounds);
    try {
      targetBounds = new mapboxgl.LngLatBounds(bounds);
    } catch (error) {
      console.error("alignMapToBounds: unable to normalize bounds", error, bounds);
      return;
    }
  }

  if (typeof targetBounds.isEmpty === "function" && targetBounds.isEmpty()) {
    console.warn("alignMapToBounds: bounds are empty after normalization");
    return;
  }

  window.mapState.bounds = targetBounds.clone ? targetBounds.clone() : targetBounds;
  const mapProjection = window.mapState.projection || "mercator";
  const maxZoom = mapProjection === "globe" ? 3.2 : 5;

  const southWest = targetBounds.getSouthWest();
  const northEast = targetBounds.getNorthEast();
  if (
    !isValidCoordinatePair(southWest.lng, southWest.lat) ||
    !isValidCoordinatePair(northEast.lng, northEast.lat)
  ) {
    console.warn("alignMapToBounds: invalid bounds detected", targetBounds);
    return;
  }

  try {
    window.mapState.map.fitBounds(targetBounds, {
      padding: { top: 80, bottom: 80, left: 80, right: 80 },
      maxZoom,
      duration: 1000,
      essential: true
    });
  } catch (error) {
    console.error("alignMapToBounds: unable to fit bounds", error, targetBounds);
  }
}

export {
  initializeMap,
  setupMarkers,
  setupScrollListener,
  setupLinkMouseEvents,
  setActiveLink,
  resetToAllMarkers
};
